import { useEffect, useRef, type ReactNode, type Ref } from 'react';
import { useTranslation } from 'react-i18next';
import { useTelegramBackButton } from '../../hooks/useTelegramBackButton';
import { cn } from '../cn';

type ModalFrameVariant = 'dialog' | 'sheet';

export interface ModalFrameProps {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  variant?: ModalFrameVariant;
  title?: string;
  showBackButton?: boolean;
  onBack?: () => void;
  className?: string;
  headerClassName?: string;
  backdropClassName?: string;
  hideHeader?: boolean;
  renderHeader?: () => ReactNode;
  closeOnBackdropClick?: boolean;
  scrollContainerRef?: Ref<HTMLDivElement>;
}

const backdropVariantClass: Record<ModalFrameVariant, string> = {
  dialog: 'items-center',
  sheet: 'items-end',
};

const panelVariantClass: Record<ModalFrameVariant, string> = {
  dialog: 'rounded-2xl',
  sheet: 'rounded-t-2xl',
};

export default function ModalFrame({
  isOpen,
  onClose,
  children,
  variant = 'sheet',
  title,
  showBackButton = false,
  onBack,
  className,
  headerClassName,
  backdropClassName,
  hideHeader,
  renderHeader,
  closeOnBackdropClick = true,
  scrollContainerRef,
}: ModalFrameProps) {
  const { t } = useTranslation();
  const openTimeRef = useRef<number>(0);

  useTelegramBackButton(isOpen && showBackButton, () => {
    if (onBack) {
      onBack();
    } else {
      onClose();
    }
  });

  useEffect(() => {
    if (isOpen) {
      openTimeRef.current = Date.now();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className={cn(
        'fixed inset-0 z-[2000] flex justify-center bg-scrim px-xl',
        backdropVariantClass[variant],
        backdropClassName,
      )}
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={() => {
        // Запобігаємо закриттю модалки одразу після відкриття (через event bubbling від кліку на маркер)
        if (Date.now() - openTimeRef.current < 100) {
          return;
        }
        // Перевіряємо, чи дозволено закривати при кліку на backdrop
        if (closeOnBackdropClick) {
          onClose();
        }
      }}
    >
      <div
        ref={scrollContainerRef}
        className={cn(
          'relative z-[2001] max-h-[85vh] w-full max-w-[520px] overflow-y-auto bg-surface-base px-2xl pt-0 pb-4xl text-white shadow-[0_-12px_35px_var(--color-black-alpha-45)]',
          panelVariantClass[variant],
          className,
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {renderHeader && renderHeader()}
        {!renderHeader && !hideHeader && (
          <header
            className={cn(
              'sticky top-0 z-[2010] mb-xl flex items-center justify-between bg-[image:var(--gradient-modal-header)] pt-2xl pb-xl text-[14px] font-normal text-white',
              headerClassName,
            )}
          >
            <div className="h-[36px] w-[36px] shrink-0" />

            {title && (
              <div className="flex flex-1 items-center justify-center text-center">
                <p className="m-0 max-w-[280px]">
                  {title}
                </p>
              </div>
            )}
            {!title && <div className="flex-1" />}

            <button
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              className="h-[36px] w-[36px] shrink-0 cursor-pointer border-none bg-transparent p-0 text-center text-[24px] leading-[36px] text-white"
              aria-label={t("Close")}
            >
              ×
            </button>
          </header>
        )}

        {children}
      </div>
    </div>
  );
}
